import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@dashboard/lib/utils";
import { resetPassword } from "aws-amplify/auth";

type EmailVerificationFormProps = {
  verificationCodeSent: (step: "request" | "confirm", email?: string) => Promise<void>;
  email: string;
};

const emailSchema = z.object({
  email: z
    .string({ required_error: "Email is required" })
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email address" }),
});

type EmailFormValues = z.infer<typeof emailSchema>;

export const EmailVerificationForm = ({
  verificationCodeSent,
  email,
}: EmailVerificationFormProps) => {
  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
  });

  const sendCode: SubmitHandler<EmailFormValues> = async ({ email }) => {
    try {
      await resetPassword({ username: email.trim().toLowerCase() });
      verificationCodeSent("confirm", email.trim().toLowerCase());
    } catch (error: any) {
      if (error.name === "UserNotFoundException") {
        emailForm.setError("email", {
          type: "custom",
          message: "Account not found",
        });
      } else if (error.name === "LimitExceededException") {
        emailForm.setError("email", {
          type: "custom",
          message: "You have exceeded the limit. Please try after some time.",
        });
      }
    }
  };

  return (
    <FormProvider {...emailForm}>
      <form onSubmit={emailForm.handleSubmit(sendCode)} autoComplete="off" className="space-y-4">
        <div className="mb-4">
          <input
            type="text"
            placeholder="Email"
            autoComplete="off"
            {...emailForm.register("email")}
            value={email != "" ? email : emailForm.watch("email")}
            disabled={email != "" ? true : false}
            className="w-full rounded-md border border-[#E5E7EB] px-4 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {emailForm.formState.errors.email && (
            <p className="text-sm text-red-500">{emailForm.formState.errors.email.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={emailForm.formState.isSubmitting}
          className={cn(
            "flex h-10 w-full cursor-pointer items-center justify-center rounded-md bg-[#0066FF] font-semibold text-white transition hover:bg-[#0056d6]",
            {
              "cursor-not-allowed bg-[#3A72C4]": emailForm.formState.isSubmitting,
            }
          )}
        >
          <span className="flex items-center justify-center gap-2">
            {emailForm.formState.isSubmitting ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                Sending...
              </>
            ) : (
              "Send Verification Code"
            )}
          </span>
        </button>
      </form>
    </FormProvider>
  );
};
